import { DateTime } from "luxon";

import type { ApiChannelProvider } from "@app/analytics/types";

import type {
  IntegrationSetting,
  IntegrationSettingsKind,
} from "./integrationSettingsTypes";
import type {
  IntegrationProviderCategory,
  IntegrationProviderId,
  IntegrationProviderKind,
  ProductivityProviderId,
} from "./providerTypes";

type IntegrationSyncMessageKind = "topic" | "session";

export type IntegrationScope = "user" | "workspace";

export type IntegrationAlertKind =
  | "error"
  | "microsoft_admin_consent_required"
  | "microsoft_admin_consent_granted_reauth_required"
  | "reauth_required";

export type IntegrationConversationKind =
  | "public_channel"
  | "private_channel"
  | "mpim"
  | "im";

export interface IntegrationAlert {
  kind: IntegrationAlertKind;
  message: string;
}

/** API Types */
export interface ApiIntegration {
  kind: IntegrationProviderId;
  connected: boolean;
  scope: IntegrationScope;
  alerts: IntegrationAlert[];
}

export interface ApiSyncAllIntegrationRequestBody {
  messageKind: IntegrationSyncMessageKind;
  integrationKind: IntegrationSettingsKind;
}

export interface ApiSyncIntegrationRequestBody
  extends ApiSyncAllIntegrationRequestBody {
  intervalId: string;
}

export interface ApiProductivityIntegrationStatus {
  source: ApiChannelProvider;
  firstRunSucceeded?: boolean;
  latestLastSyncTime?: string;
  channelCount?: number;
  messageCount?: number;
  actionItemCount?: number;
  keyQuestionCount?: number;
  topicReportIds?: string[];
  /* Date at which the user's access to this provider's channel(s) was granted.
  Should correspond to when the user connected this provider */
  userAccessCreation?: string;
}

/** Parsed/FE-only types */
export interface Integration {
  providerId: IntegrationProviderId;
  kind: IntegrationProviderKind;
  alerts: IntegrationAlert[];
  category: IntegrationProviderCategory;
  displayName: string;
  displayNameShort: string;
  documentationUrl?: string;
  icon: string;
  isConnected: boolean;
  isDisabled: boolean;
  isPremium: boolean;
  integrationSettings: {
    byId: { [key in IntegrationSettingsKind]?: IntegrationSetting };
    all: IntegrationSetting[];
  };
  limitedData: boolean;
  providerEmail?: string;
  route: string;
  reauthRequired: boolean;
  scope: IntegrationScope;
}

export interface IntegrationConversation {
  id: string;
  name: string;
  kind: IntegrationConversationKind;
}

export enum ProductivityIntegrationStatusStep {
  INITIAL = 1,
  INGESTION = 2,
  REPORT_GENERATION = 3,
  COMPLETE = 4,
}

export interface ProductivityIntegrationStatus {
  provider: ProductivityProviderId;
  lastSyncTime?: DateTime;
  numChannels: number | null;
  numMessages: number | null;
  numActionItems: number | null;
  numKeyQuestions: number | null;
  topicReportIds: string[];
  createdOn?: DateTime;
  step: ProductivityIntegrationStatusStep;
  error: boolean;
}
